import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Flex,
  Form,
  Grid,
  Input,
  Modal,
  Pagination,
  Popconfirm,
  Popover,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
const { Text } = Typography;
import { Icon } from "@iconify/react";
import { format } from "date-fns";
import useTitleStore from "../../../store/titleStore";
import pallete from "../../../utils/pallete";
import DetailSection from "../components/DetailSection";
import useDebounce from "../../../hooks/useDebounce";
import { useTableHeight } from "../../../hooks/useTableHeight";
import UpdatedByCell from "../../../components/UpdatedByCell";
import { migranCenterService } from "../../../services/migran-center.service";
import { fetchProvinces } from "../../../services/province.service";

export default function MigranCenterIndex() {
  const breakpoint = Grid.useBreakpoint();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setTitle } = useTitleStore();
  const tableHeight = useTableHeight(430);
  const [messageApi, contextHolder] = message.useMessage();

  // Filter state for Migran Center
  const [mcKeyword, setMcKeyword] = useState("");
  const debouncedMcKeyword = useDebounce(mcKeyword, 500);
  const [mcStatus, setMcStatus] = useState(undefined);
  const [mcProvince, setMcProvince] = useState(undefined);
  const [mcParams, setMcParams] = useState({
    limit: 10,
    page: 1,
    query: undefined,
    isActive: undefined,
    province: undefined,
  });

  const [exportLoading, setExportLoading] = useState(false);

  // Modal states
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [addForm] = Form.useForm();

  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Set page title
  useEffect(() => {
    setTitle("Migrant Center");
  }, [setTitle]);

  // Fetch provinces for filter
  const { data: provinces = [] } = useQuery({
    queryKey: ["provinces"],
    queryFn: fetchProvinces,
    staleTime: 24 * 60 * 60 * 1000,
  });

  const provinceOptions = useMemo(
    () =>
      provinces.map((prov) => ({
        label: prov.nama,
        value: prov.nama,
      })),
    [provinces],
  );

  // Sync Migran Center params
  useEffect(() => {
    setMcParams((prev) => ({
      ...prev,
      page: 1,
      query: debouncedMcKeyword || undefined,
      isActive:
        mcStatus !== undefined && mcStatus !== "" ? mcStatus : undefined,
      province: mcProvince || undefined,
    }));
  }, [debouncedMcKeyword, mcStatus, mcProvince]);

  // Fetch Migran Center list
  const {
    data: migranCenterData,
    isLoading: isMcLoading,
    isRefetching: isMcRefetching,
  } = useQuery({
    queryKey: ["migran-center", mcParams],
    queryFn: () => migranCenterService.fetchAll(mcParams),
    placeholderData: keepPreviousData,
  });

  // Fetch Eligible Institutions list (for selection dropdown when adding)
  const { data: eligibleData, isLoading: isEligibleLoading } = useQuery({
    queryKey: ["migran-center-eligible"],
    queryFn: () => migranCenterService.fetchEligibleInstitutions({ all: true }),
  });

  // Parse lists
  const mcList = useMemo(() => {
    if (!migranCenterData) return [];
    if (Array.isArray(migranCenterData)) return migranCenterData;
    if (Array.isArray(migranCenterData?.data)) return migranCenterData.data;
    return [];
  }, [migranCenterData]);

  const eligibleList = useMemo(() => {
    if (!eligibleData) return [];
    if (Array.isArray(eligibleData)) return eligibleData;
    if (Array.isArray(eligibleData?.data)) return eligibleData.data;
    return [];
  }, [eligibleData]);

  // Map of institution IDs that already registered as Migran Center
  const registeredInstitutionIds = useMemo(() => {
    const ids = new Set();
    mcList.forEach((item) => {
      if (item.vocationalInstitutionId)
        ids.add(String(item.vocationalInstitutionId));
      if (item.vocationalInstitution?.id)
        ids.add(String(item.vocationalInstitution.id));
      if (item.vocationalInstitution?.uuid)
        ids.add(String(item.vocationalInstitution.uuid));
      if (item.institutionId) ids.add(String(item.institutionId));
      if (item.id) ids.add(String(item.id));
      if (item.uuid) ids.add(String(item.uuid));
    });
    return ids;
  }, [mcList]);

  // Eligible options for dropdown (filter out already registered ones)
  const eligibleSelectOptions = useMemo(() => {
    return eligibleList
      .filter((inst) => {
        const idToCheck = String(inst.id || inst.uuid || inst.institutionId);
        return !registeredInstitutionIds.has(idToCheck);
      })
      .map((inst) => {
        const id = inst.id || inst.uuid;
        return {
          label: `${inst.name}`,
          value: id,
        };
      });
  }, [eligibleList, registeredInstitutionIds]);

  // Mutations
  const addMutation = useMutation({
    mutationFn: (payload) => migranCenterService.store(payload),
    onSuccess: (res) => {
      messageApi.success(
        res?.message || "Lembaga berhasil ditambahkan sebagai Migran Center",
      );
      queryClient.invalidateQueries({ queryKey: ["migran-center"] });
      queryClient.invalidateQueries({ queryKey: ["migran-center-eligible"] });
      setAddModalVisible(false);
      addForm.resetFields();
    },
    onError: (err) => {
      const errMsg =
        err?.response?.data?.message ||
        "Gagal menambahkan Lembaga ke Migran Center";
      messageApi.error(errMsg);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => migranCenterService.update(id, data),
    onSuccess: () => {
      messageApi.success("Status Migrant Center berhasil diperbarui");
      queryClient.invalidateQueries({ queryKey: ["migran-center"] });
      queryClient.invalidateQueries({ queryKey: ["migran-center-eligible"] });
    },
    onError: (err) => {
      const errMsg =
        err?.response?.data?.message ||
        "Gagal memperbarui status Migrant Center";
      messageApi.error(errMsg);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => migranCenterService.delete(id),
    onSuccess: () => {
      messageApi.success("Lembaga berhasil dihapus dari Migran Center");
      queryClient.invalidateQueries({ queryKey: ["migran-center"] });
      queryClient.invalidateQueries({ queryKey: ["migran-center-eligible"] });
      setDetailModalVisible(false);
    },
    onError: (err) => {
      const errMsg =
        err?.response?.data?.message || "Gagal menghapus data Migran Center";
      messageApi.error(errMsg);
    },
  });

  // Handle Export Excel
  const handleExportExcel = async () => {
    try {
      setExportLoading(true);
      const response = await migranCenterService.exportExcel({
        query: mcParams.query,
        isActive: mcParams.isActive,
        province: mcParams.province,
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const fileName =
        response.headers["content-disposition"]
          ?.split("filename=")?.[1]
          ?.replace(/"/g, "") || "migran-center.xlsx";
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      messageApi.success("File berhasil diunduh");
    } catch {
      messageApi.error("Gagal mengunduh file Excel");
    } finally {
      setExportLoading(false);
    }
  };

  const handleOpenAddModal = () => {
    addForm.resetFields();
    setAddModalVisible(true);
  };

  const handleConfirmAdd = async () => {
    try {
      const values = await addForm.validateFields();
      addMutation.mutate({
        vocationalInstitutionIds: values.vocationalInstitutionIds,
        isActive: true,
      });
    } catch (err) {
      console.error(err);
    }
  };

  // Open detail modal
  const handleOpenDetail = async (record) => {
    const id = record.uuid || record.id;
    setDetailModalVisible(true);
    setSelectedDetail(record);
    if (id) {
      try {
        setDetailLoading(true);
        const detailRes = await migranCenterService.findById(id);
        if (detailRes?.data) {
          setSelectedDetail(detailRes.data);
        }
      } catch (err) {
        console.error("Failed to load detail", err);
      } finally {
        setDetailLoading(false);
      }
    }
  };

  // Helper getters
  const inst = selectedDetail?.vocationalInstitution;
  const getInstName = (r) =>
    r.vocationalInstitution?.name || r.institutionName || r.name || "-";
  const getInstCode = (r) =>
    r.vocationalInstitution?.institutionId ||
    r.institutionId ||
    r.vocationalInstitutionId ||
    "-";
  const getInstType = (r) =>
    r.vocationalInstitution?.type || r.type || "Universitas";
  const getInstForm = (r) =>
    r.vocationalInstitution?.institutionForm || r.institutionForm || "-";

  // Migran Center Columns
  const mcColumns = [
    {
      title: "No",
      align: "center",
      width: 55,
      fixed: breakpoint.md && "left",
      render: (_, __, index) => {
        const page = migranCenterData?.pagination?.page || mcParams.page || 1;
        const limit = mcParams.limit || 10;
        return (page - 1) * limit + index + 1;
      },
    },
    {
      title: "ID & Nama Lembaga",
      dataIndex: "name",
      width: 300,
      fixed: breakpoint.md && "left",
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <Typography.Text strong style={{ fontSize: 14 }}>
            {getInstName(record)}
          </Typography.Text>
          <Typography.Text
            type="secondary"
            style={{ fontSize: 12, fontStyle: "italic" }}
          >
            {getInstCode(record)}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: "Jenis & Status Lembaga",
      width: 120,
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <Typography.Text strong>{getInstType(record)}</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {getInstForm(record)}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: "Provinsi & Kota Lembaga",
      dataIndex: "province",
      width: 120,
      render: (_, record) => (
        <>
          <Typography.Text strong>
            {record.vocationalInstitution?.province ?? "-"}
          </Typography.Text>
          <br />
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>
            {record.vocationalInstitution?.district ?? "-"}
          </Typography.Text>
        </>
      ),
    },
    {
      title: "Status Migrant Center",
      dataIndex: "isActive",
      width: 120,
      render: (isActive, record) => {
        const active = Boolean(isActive);
        const recordId = record.uuid || record.id;
        return (
          <Tag color={active ? "success" : "default"}>
            {active ? "Aktif" : "Nonaktif"}
          </Tag>
        );
      },
    },
    {
      title: "Update By",
      width: 150,
      render: (record) => <UpdatedByCell value={record} />,
    },
    {
      className: "last-cell-p",
      title: "Aksi",
      align: "right",
      fixed: "right",
      width: 80,
      render: (record) => (
        <Button
          icon={<Icon icon="solar:eye-bold-duotone" width={18} height={18} />}
          color="default"
          variant="filled"
          onClick={() => handleOpenDetail(record)}
        />
      ),
    },
  ];

  const activeFilterCount = [mcStatus, mcProvince].filter(Boolean).length;

  const filterContent = (
    <div style={{ width: breakpoint.sm ? 460 : 280, padding: "4px 0" }}>
      <Flex justify="space-between" align="center" style={{ marginBottom: 12 }}>
        <Typography.Text strong style={{ fontSize: 14 }}>
          Filter Migrant Center
        </Typography.Text>
        {activeFilterCount > 0 && (
          <Button
            type="link"
            size="small"
            danger
            style={{ padding: 0, height: "auto" }}
            onClick={() => {
              setMcStatus(undefined);
              setMcProvince(undefined);
            }}
          >
            Reset
          </Button>
        )}
      </Flex>

      <Row gutter={[12, 12]}>
        <Col xs={24} sm={12}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Status Aktif
          </Typography.Text>
          <Select
            allowClear
            placeholder="Semua Status"
            style={{ width: "100%", marginTop: 4 }}
            value={mcStatus}
            onChange={(val) => setMcStatus(val)}
            options={[
              { label: "Semua Status", value: "" },
              { label: "Aktif", value: "true" },
              { label: "Nonaktif", value: "false" },
            ]}
          />
        </Col>
        <Col xs={24} sm={12}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Provinsi
          </Typography.Text>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Semua Provinsi"
            style={{ width: "100%", marginTop: 4 }}
            value={mcProvince}
            onChange={(val) => setMcProvince(val)}
            options={provinceOptions}
          />
        </Col>
      </Row>
    </div>
  );

  return (
    <>
      {contextHolder}
      <Card
        styles={{
          body: {
            paddingInline: 24,
            paddingTop: 24,
            paddingBottom: 24,
          },
        }}
      >
        {/* Filters Bar */}
        <Flex
          justify="space-between"
          align="center"
          gap={12}
          wrap="wrap"
          style={{ marginBottom: 20 }}
        >
          <Input
            style={{ width: breakpoint.md ? 280 : "100%", minWidth: 200 }}
            prefix={<Icon icon="hugeicons:search-01" width={16} height={16} />}
            placeholder="Cari nama lembaga / ID..."
            allowClear
            value={mcKeyword}
            onChange={(e) => setMcKeyword(e.target.value)}
          />

          <Flex gap={10} wrap="wrap">
            <Popover
              content={filterContent}
              trigger="click"
              placement="bottomRight"
            >
              <Badge count={activeFilterCount} size="small" offset={[-2, 2]}>
                <Button
                  icon={
                    <Icon
                      icon="solar:filter-bold-duotone"
                      width={18}
                      height={18}
                    />
                  }
                  type={activeFilterCount > 0 ? "primary" : "default"}
                  ghost={activeFilterCount > 0}
                >
                  Filter
                </Button>
              </Badge>
            </Popover>
            <Button
              icon={
                <Icon icon="solar:export-bold-duotone" width={18} height={18} />
              }
              loading={exportLoading}
              onClick={handleExportExcel}
            >
              Export Excel
            </Button>
            <Button
              type="primary"
              icon={
                <Icon icon="solar:add-circle-bold" width={18} height={18} />
              }
              onClick={handleOpenAddModal}
            >
              {breakpoint.md ? "Tambah Migran Center" : "Tambah"}
            </Button>
          </Flex>
        </Flex>

        {/* Table */}
        <Table
          columns={mcColumns}
          size="small"
          loading={isMcLoading || isMcRefetching}
          rowKey={(r) => r.uuid || r.id || String(Math.random())}
          dataSource={mcList}
          pagination={false}
          scroll={{ y: tableHeight, x: 1100 }}
        />

        {/* Pagination */}
        <Pagination
          style={{ marginTop: 16 }}
          total={migranCenterData?.pagination?.total || mcList.length}
          pageSize={mcParams.limit}
          current={migranCenterData?.pagination?.page || mcParams.page}
          onChange={(page, limit) =>
            setMcParams((prev) => ({ ...prev, page, limit }))
          }
          showSizeChanger={{ showSearch: false }}
          pageSizeOptions={["10", "20", "50"]}
          align="end"
        />
      </Card>

      {/* Modal: Jadikan Migran Center */}
      <Modal
        title={
          <Flex align="center" gap={8}>
            <Icon
              icon="solar:diploma-verified-bold"
              width={22}
              height={22}
              color={pallete.primary?.[500]}
            />
            <span>Tambahkan Migrant Center</span>
          </Flex>
        }
        open={addModalVisible}
        onCancel={() => {
          setAddModalVisible(false);
          addForm.resetFields();
        }}
        onOk={handleConfirmAdd}
        okText="Tambah Migran Center"
        cancelText="Batal"
        confirmLoading={addMutation.isPending}
        destroyOnHidden
      >
        <Form form={addForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="vocationalInstitutionIds"
            label={
              <>
                <Text>Pilih Universitas</Text>
                <Text style={{ color: "#940808" }}>*</Text>
              </>
            }
            extra="Dapat memilih lebih dari satu universitas"
            rules={[
              {
                required: true,
                type: "array",
                min: 1,
                message: "Pilih minimal satu Universitas",
              },
            ]}
            required={false}
          >
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              maxTagCount="responsive"
              placeholder="Cari dan Pilih Universitas"
              loading={isEligibleLoading}
              options={eligibleSelectOptions}
            />
          </Form.Item>
        </Form>

        <Alert
          message={
            <Text style={{ fontSize: 12, fontWeight: 600, marginBottom: 0 }}>
              Catatan
            </Text>
          }
          type="warning"
          description={
            <Text style={{ fontSize: 12 }}>
              Lembaga yang dapat dipilih adalah lembaga vokasi berjenis
              Universitas dan berstatus aktif.
            </Text>
          }
          style={{ marginBottom: 32, padding: 15 }}
        />
      </Modal>

      {/* Modal: Detail Migran Center */}
      <Modal
        title="Detail Informasi Lembaga"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Popconfirm
            key="delete"
            title="Hapus Migrant Center"
            description="Apakah Anda yakin ingin menghapus lembaga ini dari Migrant Center?"
            okText="Hapus"
            cancelText="Batal"
            okButtonProps={{ danger: true, loading: deleteMutation.isPending }}
            onConfirm={() => {
              const recordId = selectedDetail.uuid || selectedDetail.id;
              deleteMutation.mutate(recordId);
            }}
          >
            <Button danger loading={deleteMutation.isPending}>
              Hapus
            </Button>
          </Popconfirm>,
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Tutup
          </Button>,
          selectedDetail &&
            (selectedDetail.vocationalInstitution?.uuid ||
              selectedDetail.vocationalInstitutionId) && (
              <Button
                key="view-inst"
                type="primary"
                onClick={() => {
                  const targetId =
                    selectedDetail.vocationalInstitution?.uuid ||
                    selectedDetail.vocationalInstitutionId;
                  navigate(`/institutional-verification/detail/${targetId}`);
                }}
              >
                Lihat Detail Lembaga
              </Button>
            ),
        ]}
        loading={detailLoading}
        width={760}
      >
        {selectedDetail && (
          <div style={{ marginTop: 12, marginBottom: 32 }}>
            <DetailSection
              title="Informasi Profil Lembaga"
              items={[
                {
                  label: "Nama Lembaga",
                  span: 2,
                  children: getInstName(selectedDetail),
                },
                {
                  label: "ID Vokasi Lembaga",
                  span: 2,
                  children: getInstCode(selectedDetail),
                },
                {
                  label: "Jenis Lembaga",
                  children: getInstType(selectedDetail),
                },
                {
                  label: "Status Lembaga",
                  children: inst?.institutionStatus || "-",
                },
              ]}
            />
            <DetailSection
              title="Informasi Pimpinan Lembaga"
              items={[
                {
                  label: "Nama Pimpinan",
                  span: 2,
                  children: inst?.leaderName || "-",
                },
                {
                  label: "Jabatan",
                  span: 2,
                  children: inst?.leaderPosition || "-",
                },
              ]}
            />
            <DetailSection
              title="Informasi Alamat Lembaga"
              items={[
                {
                  label: "Alamat Lengkap",
                  span: 2,
                  children: inst?.address || "-",
                },
                { label: "Provinsi", children: inst?.province || "-" },
                { label: "Kota/Kabupaten", children: inst?.district || "-" },
                {
                  label: "Kode Pos",
                  span: 2,
                  children: inst?.postalCode || "-",
                },
              ]}
            />
            <DetailSection
              title="Informasi Kontak Lembaga"
              items={[
                {
                  label: "Alamat Email",
                  span: 2,
                  children: inst?.email || "-",
                },
                {
                  label: "Nomor Telepon",
                  span: 2,
                  children: inst?.phone || "-",
                },
                {
                  label: "Website/Sosial Media",
                  span: 2,
                  children: inst?.website || "-",
                },
                {
                  label: "Link INAPROC",
                  span: 2,
                  children: inst?.inaprocLink || "-",
                },
              ]}
            />
            <DetailSection
              title="Informasi PIC Lembaga"
              items={[
                {
                  label: "Nama Lengkap",
                  span: 2,
                  children: inst?.pic?.fullName || "-",
                },
                { label: "Jabatan", children: inst?.pic?.position || "-" },
                { label: "Nomor Telepon", children: inst?.pic?.phone || "-" },
              ]}
            />
            <DetailSection
              title="Status Aktivasi Migrant Center Lembaga"
              items={[
                {
                  label: "Status Migrant Center",
                  span: 2,
                  children: (
                    <Switch
                      size="small"
                      checked={Boolean(selectedDetail.isActive)}
                      loading={updateMutation.isPending}
                      onChange={(checked) => {
                        const recordId =
                          selectedDetail.uuid || selectedDetail.id;
                        updateMutation.mutate(
                          { id: recordId, data: { isActive: checked } },
                          {
                            onSuccess: () => {
                              setSelectedDetail((prev) => ({
                                ...prev,
                                isActive: checked,
                              }));
                            },
                          },
                        );
                      }}
                      checkedChildren="Aktif"
                      unCheckedChildren="Nonaktif"
                    />
                  ),
                },
                {
                  label: "Terakhir Diperbarui",
                  children: selectedDetail.updatedAt
                    ? format(
                        new Date(selectedDetail.updatedAt),
                        "dd/MM/yyyy HH:mm",
                      )
                    : "-",
                },
                {
                  label: "Diperbarui Oleh",
                  children:
                    selectedDetail.updatedBy?.fullName ||
                    (typeof selectedDetail.updatedBy === "string"
                      ? selectedDetail.updatedBy
                      : "-"),
                },
              ]}
            />
          </div>
        )}
      </Modal>
    </>
  );
}
